<template>
  <div class="form">
    <div class="form__content">
      <h1 class="extra-large-headline">Let's verify your details.</h1>
      <!-- <p>
        Complete the details below and we'll send you a
        {{ pinCodeLength }} digit code to verify you.
      </p> -->
    </div>
    <form @submit.prevent>
      <EmailField
        v-model:email="form.email"
        :errors="store.getErrorByField('email')"
      />

      <MobileField
        v-if="showMobileFields()"
        v-model:mobile="form.mobile"
        @dropdownValueUpdate="updateDropdownValues"
        :countries="countryList"
        :defaultValues="defaultValuesOnInt"
        :errors="store.getErrorByField('mobile')"
      />

      <AutocompleteSelect
        v-model:autocomplete="form.country"
        :label="'Country of residence*'"
        :placeholder="'Search a country'"
        :type="'text'"
        :items="countryList"
        :isLoading="isFetchingData"
        :errors="store.getErrorByField('country')"
        hasIcon
        @update:autocomplete="updateSelectedSingle"
      />

      <Checkbox
        name="marketing"
        text="Send me offers and updates from Banxa."
        v-model:value="form.marketing"
        v-model:marketing="form.marketing"
      />

      <Checkbox
        name="tnc"
        text="I have read and agree to the <a target='_blank' href='https://banxa.com/terms-of-use/' rel='nofollow'>terms & conditions</a> and acknowledge the <a target='_blank' href='https://banxa.com/privacy-policy/' rel='nofollow'>Privacy and Cookies policy</a>.*"
        v-model:value="form.tnc"
        v-model:tnc="form.tnc"
        :errors="store.getErrorByField('tnc')"
      />

      <checkbox
        name="consumerReport"
        text="I authorise Banxa to obtain my consumer reports from consumer reporting agencies for the purposes of fraud prevention and compliance with laws.*"
        v-model:value="form.consumerReport"
        v-model:consumerReport="form.consumerReport"
        :errors="store.getErrorByField('consumerReport')"
        v-if="!orderStore.isNFTMode"
      />

      <ButtonBase
        :isDisabled="store.formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        Submit my verification
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { getCountryCallingCode } from "libphonenumber-js";

import { useGlobalStore } from "@/stores/globalStore";
import { useVerificationStore } from "@/stores/forms/verificationFormStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";

import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useApiHelpers from "@/composables/api/helpers";
import useProgressBarActions from "@/composables/progress/useProgressBarActions";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";
import { isValueString } from "@/composables/helpers/useStringHelpers.js";
import useCustomerActions from "@/composables/api/useCustomerActions";

import AutocompleteSelect from "@/components/ui/selects/AutocompleteSelect.vue";
import EmailField from "@/components/ui/inputs/EmailField.vue";
import MobileField from "@/components/ui/inputs/MobileField.vue";
import Checkbox from "@/components/ui/inputs/Checkbox.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";

const router = useRouter();
const store = useVerificationStore();
const globalStore = useGlobalStore();
const orderStore = useOrderFormStore();
const { fetchCountries } = useGlobalCountriesActions();
const { getRequest, postRequest } = useApiHelpers();
const { watchAndUpdateStore } = useWatchAndUpdate();
const { updateToPending } = useProgressBarActions();
const identityQueueStore = useIdentityQueueStore();
const { fetchCustomerAccountInfo, fillForm } = useCustomerActions();

const pinCodeLength = import.meta.env.VITE_APP_PINCODE_LENGTH;
const form = ref({
  email: store.form.email.value || "",
  country: store.form.country.value || "Australia",
  marketing: store.form.marketing.value || false,
  mobile: store.form.mobile?.value || "",
  internationalCode: store.form.internationalCode?.value || "+61",
  phoneCountry: store.form.phoneCountry?.value || "AU",
  tnc: false,
  consumerReport: false,
});
const defaultValuesOnInt = ref({});
const countryList = ref([]);
const isFetchingData = ref(true);

const updateDropdownValues = (dropdownData) => {
  const selectedCountry = countryList.value.find(country => country.code === dropdownData.code);
  setFormData(selectedCountry);
};

const updateSelectedSingle = (selectedItem) => {
  if (isEnableMobile() && countryList.value.length) {
    const selectedCountry = countryList.value.find(country => country.name === selectedItem.value);
    setFormData(selectedCountry);
  }
}

const isEnableMobile = () => {
  return globalStore.getMerchantSettings.VALIDATE_MSISDN || orderStore.getPaymentObject.require_mobile 
      || (!globalStore.getMerchantSettings.VALIDATE_MSISDN && !globalStore.getMerchantSettings.VALIDATE_EMAIL);
}

const setFormData = (data) => {
  Object.keys(data).forEach((el) => {
    if (el === 'name') {
        form.value.country = data[el];
    } else {
      if (form.value[el]) {
        const intCode = el === "internationalCode" ? "+" : "";
        form.value[el] = `${intCode}${data[el]}`;
      }
    }
  });

  /**
   * Initially countries are not yet formatted
   * We need to set internationalCode and phoneCountry
   */
  if (!data['internationalCode']) {
    form.value.internationalCode = `+${getCountryCallingCode(data.code)}`;
    form.value.phoneCountry = data.code;
  }

  defaultValuesOnInt.value = setMobileFieldValue();
}

const setMobileFieldValue = () => {
  return {
    internationalCode: form.value.internationalCode,
    phoneCountry: form.value.phoneCountry,
    country: form.value.country,
    mobile: form.value.mobile
  }
}

const handleFormRedirect = async (response) => {
  const { data } = response;
  const needToVerifyMobile =
    isValueString(data.rel.next) && data.rel.next === "UserMustVerifyMobile";

  if (needToVerifyMobile) {
    router.push("/verification/code");
  } else {
    // Check if anything is required to submit the identity
    await identityQueueStore.fetchIdentityDocuments();
    router.push(identityQueueStore.getNextIdentityRoute);
  }
};

const showMobileFields = () => {
  if (isEnableMobile()) {
    store.reAddField("internationalCode");
    store.reAddField("phoneCountry");
    store.reAddField("mobile");
    return true;
  }
  store.removeField("internationalCode");
  store.removeField("phoneCountry");
  store.removeField("mobile");
  return false;
};

const handleFormSubmit = () => {
  globalStore.setIsFormLoading(true);
  const { phoneCountry, mobile, internationalCode, ...rest } = form.value;
  if (!showMobileFields()) {
    postRequest("/accounts", rest, handleFormRedirect);
    return;
  }
  postRequest("/accounts", form.value, handleFormRedirect);
};

onMounted(async () => {
  updateToPending("verification");

  const formattedCountries = await fetchCountries();
 
  countryList.value = formattedCountries;

  const customerInfo = await fetchCustomerAccountInfo();
  await fillForm(customerInfo, form, store);

  isFetchingData.value = false;

  defaultValuesOnInt.value = setMobileFieldValue();
  
  if (orderStore.isNFTMode){
    form.value.consumerReport = true;
  }
});

watchAndUpdateStore(form, store, (key, val) => {
  if (key === "phoneCountry") {
    store.updateValue("mobile", form.value.mobile);
  }
});
</script>
